//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  ContentCard,
  DetailLabel,
  DetailRow,
  MultiLineValue,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/SamplingCampaignRequestQuoteSharedStyles';
import { SamplingCampaignRequestClientFulfillmentSummary } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/summary/SamplingCampaignRequestClientFulfillmentSummary';
import { SamplingCampaignRequestDeliverySummary } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/summary/SamplingCampaignRequestDeliverySummary';
import { renderPackageWeight } from '@campaigns/components/campaign-requests/sampling-campaigns/shared/sections/packaging-section/PackagingSection';
import {
  BOX_OPTIONS,
  getPackageInsertOptions,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyBoldText,
  BodyText,
  CaptionText,
} from '@components/atoms/text/Text';
import VerticalDivider from '@components/atoms/vertical-divider/VerticalDivider';
import { isDefined } from '@utils/filterPredicates';

export const SamplingCampaignRequestQuoteSummary = () => {
  const { samplingCampaignRequest } = useSamplingCampaign();

  if (!isDefined(samplingCampaignRequest)) {
    return null;
  }

  const {
    audienceDetails,
    bvClientId,
    fulfillmentOptions = {},
    memberTasks = {},
    products = [],
  } = samplingCampaignRequest;

  const {
    boxType,
    fulfillmentType,
    packageInsert,
    packageWeight,
    participantCount,
    whoShipsProduct,
  } = fulfillmentOptions;

  const totalUnits = products.reduce(
    (total, product) => total + (product.quantity ?? 0),
    0,
  );

  const boxLabel = isDefined(boxType) ? BOX_OPTIONS[boxType].label : undefined;
  const packagingDescription = `${participantCount} ${boxLabel}`;

  const packageInsertOptions = getPackageInsertOptions(bvClientId);
  const insertDescription = isDefined(packageInsert)
    ? packageInsertOptions[packageInsert].label
    : undefined;

  const country = audienceDetails?.country;

  const weightDescription = renderPackageWeight(packageWeight);

  const shouldShowReviews = memberTasks.writeReview;
  const shouldShowSocial = memberTasks.postOnSocial;
  const shouldShowPostSurvey = memberTasks.postSurvey;

  const shouldShowDeliverySummary =
    whoShipsProduct === 'bazaarvoice' && fulfillmentType === 'shipped';
  const shouldShowClientFulfillmentSummary =
    whoShipsProduct === 'myCompany' && fulfillmentType === 'shipped';
  const shouldShowFulfillmentSummary =
    shouldShowDeliverySummary || shouldShowClientFulfillmentSummary;

  return (
    <Container>
      <CardContainer>
        <ContentCard>
          <BodyBoldText>Content</BodyBoldText>

          <DetailRow>
            <DetailLabel>
              <Icon name='product-bag' />
              <CaptionText>Units</CaptionText>
            </DetailLabel>
            <BodyText>{totalUnits}</BodyText>
          </DetailRow>

          <DetailRow>
            <DetailLabel>
              <Icon name='media' />
              <CaptionText>Content</CaptionText>
            </DetailLabel>
            <MultiLineValue>
              {shouldShowReviews && <BodyText>Reviews</BodyText>}
              {shouldShowSocial && <BodyText>Social</BodyText>}
              {shouldShowPostSurvey && <BodyText>Post Survey</BodyText>}
            </MultiLineValue>
          </DetailRow>
        </ContentCard>

        {shouldShowFulfillmentSummary && <VerticalDivider />}

        {shouldShowDeliverySummary && (
          <SamplingCampaignRequestDeliverySummary
            country={country}
            insertDescription={insertDescription}
            packagingDescription={packagingDescription}
            weightDescription={weightDescription}
          />
        )}

        {shouldShowClientFulfillmentSummary && (
          <SamplingCampaignRequestClientFulfillmentSummary />
        )}
      </CardContainer>
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacings[3]};
  width: 100%;
  padding: ${theme.spacings[3]};
  background-color: ${theme.colors.gray00};
  border-radius: ${theme.spacings[1]};
`,
);

const CardContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacings[1]};
  align-self: stretch;
`,
);
